import { AxiosResponse } from 'axios';
import { Dispatch } from 'redux';
import { FOLDER_DIRECTORIES } from '../constants/folder';
import { queryClient } from '../query-client';
import foldersService from '../services/folders/folders-service';
import { foldersKeys } from '../services/folders/query-keys';
import buildFoldersTree from '../utils/build-folders-tree';

export const FOLDERS_GET = 'folders/GET';
export const FOLDERS_SUCCESS_ACTION = 'folders/succesAction';

export type FolderState = {
    actionSuccessMessage: string;
    archive?: Folder;
    list: Folder[];
    tree: Folder[];
    loaded: boolean;
    mutatedAt: number;
};

export type FoldersState = {
    [FOLDER_DIRECTORIES.MY_MATERIALS]: FolderState;
    [FOLDER_DIRECTORIES.WISHLIST]: FolderState;
    [FOLDER_DIRECTORIES.MY_UPLOADS]: FolderState;
};

const emptySlot = (): FolderState => ({
    actionSuccessMessage: '',
    list: [],
    loaded: false,
    mutatedAt: 0,
    tree: []
});

const initialState: FoldersState = {
    [FOLDER_DIRECTORIES.MY_MATERIALS]: { ...emptySlot(), archive: {} as Folder },
    [FOLDER_DIRECTORIES.WISHLIST]: emptySlot(),
    [FOLDER_DIRECTORIES.MY_UPLOADS]: emptySlot()
};

type FoldersActionsType = {
    type: string;
    payload: { directory: FOLDER_DIRECTORIES } & FolderState;
};

export default (state = initialState, action: FoldersActionsType) => {
    switch (action.type) {
        case FOLDERS_GET:
            return {
                ...state,
                [action.payload.directory]: {
                    ...state[action.payload.directory],
                    archive: action.payload.archive,
                    list: action.payload.list,
                    loaded: true,
                    mutatedAt: Date.now(),
                    tree: action.payload.tree
                }
            };
        case FOLDERS_SUCCESS_ACTION:
            return {
                ...state,
                [action.payload.directory]: {
                    ...state[action.payload.directory],
                    actionSuccessMessage: action.payload.actionSuccessMessage
                }
            };
        default:
            return state;
    }
};

type FolderDto = Folder & { is_archive?: boolean; is_shared?: boolean };

type GetFoldersOptions = {
    invalidateCounts?: boolean;
};

export const getFolders =
    (directory: FOLDER_DIRECTORIES, onError?: (error: string) => void, options?: GetFoldersOptions) =>
    (dispatch: Dispatch) => {
        foldersService
            .get(directory)
            .then((res: AxiosResponse<{ folders: FolderDto[] }>) => {
                const folders = res.data?.folders?.filter((item) => item?.id != null) ?? [];
                const nonArchive = folders.filter((item) => !item.is_archive);
                const archive = folders.find((item) => item.is_archive && item.is_shared !== true);

                dispatch({
                    payload: {
                        archive,
                        directory,
                        list: nonArchive,
                        tree: buildFoldersTree(nonArchive)
                    },
                    type: FOLDERS_GET
                });

                if (options?.invalidateCounts) {
                    queryClient.invalidateQueries({ queryKey: foldersKeys.materialCountsByDirectory(directory) });
                }
            })
            .catch((error): void => onError?.(error instanceof Error ? error.message : error));
    };

export const triggerActionSuccessMessage = (directory: FOLDER_DIRECTORIES, message: string) => (dispatch: Dispatch) => {
    dispatch({
        payload: {
            actionSuccessMessage: message,
            directory
        },
        type: FOLDERS_SUCCESS_ACTION
    });

    setTimeout(() => {
        dispatch({
            payload: {
                actionSuccessMessage: '',
                directory
            },
            type: FOLDERS_SUCCESS_ACTION
        });
    }, 8000);
};
